import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Descriptions, 
  Table, 
  Typography, 
  message, 
  Modal,
  Tabs,
  Row,
  Col,
  Input,
  Space,
  Tag
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  FilePdfOutlined, 
  MailOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  ShoppingCartOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import quotesService, { Quote } from '../../services/quotesService';
import { formatCurrency, formatQuoteReference } from '../../utils/formatters';
import QuoteStatusTag from './components/QuoteStatusTag';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const QuoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [emailModalVisible, setEmailModalVisible] = useState<boolean>(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    message: ''
  });
  const [activeTab, setActiveTab] = useState('details');
  const printRef = useRef<HTMLDivElement>(null);
  
  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  // Charger les données du devis
  const fetchQuote = useCallback(async () => {
    try {
      setLoading(true);
      if (id) {
        const data = await quotesService.getQuoteById(parseInt(id));
        setQuote(data);
        
        // Pré-remplir les données de l'email
        setEmailData({
          to: data.customerName ? `${data.customerName} <customer@example.com>` : '',
          subject: `Devis ${data.reference}`,
          message: `Bonjour,\n\nVeuillez trouver ci-joint votre devis ${data.reference} d'un montant de ${formatCurrency(data.total)}.\n\nCordialement,\nL'équipe MCT Maintenance`
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du devis:', error);
      message.error('Erreur lors du chargement du devis');
      navigate('/devis');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchQuote();
  }, [id, fetchQuote]);

  // Supprimer le devis
  const handleDelete = async () => {
    Modal.confirm({
      title: 'Supprimer le devis',
      content: 'Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est irréversible.',
      okText: 'Oui, supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          setDeleting(true);
          if (id) {
            await quotesService.deleteQuote(parseInt(id));
            message.success('Devis supprimé avec succès');
            navigate('/devis');
          }
        } catch (error) {
          console.error('Erreur lors de la suppression du devis:', error);
          message.error('Erreur lors de la suppression du devis');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  // Mettre à jour le statut du devis
  const updateStatus = async (status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired') => {
    try {
      if (id) {
        await quotesService.updateQuoteStatus(parseInt(id), status);
        message.success(`Devis marqué comme ${status}`);
        fetchQuote();
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      message.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Envoyer le devis par email
  const handleSendEmail = async () => {
    try {
      if (id) {
        await quotesService.sendByEmail(parseInt(id), emailData);
        message.success('Email envoyé avec succès');
        setEmailModalVisible(false);
        
        // Mettre à jour le statut du devis à "Envoyé" s'il est en brouillon
        if (quote?.status === 'draft') {
          updateStatus('sent');
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      message.error('Erreur lors de l\'envoi de l\'email');
    }
  };

  // Convertir le devis en commande
  const handleConvertToOrder = async () => {
    try {
      if (id) {
        const order = await quotesService.convertToOrder(parseInt(id), {});
        console.log('Commande créée:', order); // Log pour déboguer
        message.success('Commande créée avec succès');
        navigate(`/commandes/${order.id}`);
      }
    } catch (error) {
      console.error('Erreur lors de la conversion en commande:', error);
      message.error('Erreur lors de la conversion en commande');
    }
  };

  // Télécharger le PDF
  const handleDownloadPdf = async () => {
    try {
      if (id) {
        const pdfBlob = await quotesService.generatePdf(parseInt(id));
        const url = window.URL.createObjectURL(new Blob([pdfBlob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `devis-${quote?.reference}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        message.success('PDF généré avec succès');
      }
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      message.error('Erreur lors de la génération du PDF');
    }
  };

  // Colonnes du tableau des articles
  const columns = [
    {
      title: 'Produit',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
    },
    ...(!isAgent ? [
      {
        title: 'Prix unitaire',
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        width: 150,
        align: 'right' as const,
        render: (value: number) => formatCurrency(value),
      },
      {
        title: 'Remise',
        dataIndex: 'discount',
        key: 'discount',
        width: 100,
        align: 'right' as const,
        render: (value: number) => `${value || 0}%`,
      },
      {
        title: 'TVA',
        dataIndex: 'taxRate',
        key: 'taxRate',
        width: 100,
        align: 'right' as const,
        render: (value: number) => `${value || 0}%`,
      },
      {
        title: 'Total HT',
        key: 'total',
        width: 150,
        align: 'right' as const,
        render: (_: any, record: any) => {
          const total = record.quantity * record.unitPrice * (1 - (record.discount || 0) / 100);
          return formatCurrency(total);
        },
      },
    ] : []),
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Chargement...</div>;
  }

  if (!quote) {
    return <div>Devis non trouvé</div>;
  }

  // Données pour les statistiques
  const stats = [
    { title: 'Sous-total HT', value: quote.subtotal },
    { title: 'Remise', value: -quote.discountAmount },
    { title: 'TVA', value: quote.taxAmount },
    { title: 'Total TTC', value: quote.total, isTotal: true },
  ];

  return (
    <div className="quote-detail">
      <div style={{ marginBottom: 16 }}>
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/devis')}
        >
          Retour à la liste
        </Button>
      </div>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Devis {formatQuoteReference(quote.reference)}</Title>
          <Text type="secondary">
            Créé le {dayjs(quote.createdAt).format('DD/MM/YYYY')} 
            {quote.updatedAt && ` • Dernière mise à jour le ${dayjs(quote.updatedAt).format('DD/MM/YYYY')}`}
          </Text>
        </div>
        
        <Space>
          <QuoteStatusTag status={quote.status} />
          
          {!isAgent && (
            <>
              <Button 
                icon={<FilePdfOutlined />} 
                onClick={handleDownloadPdf}
              >
                PDF
              </Button>
              
              <Button 
                icon={<PrinterOutlined />}
                onClick={handlePrint}
              >
                Imprimer
              </Button>
              
              <Button 
                icon={<MailOutlined />} 
                onClick={() => setEmailModalVisible(true)}
              >
                Envoyer
              </Button>
              
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={() => navigate(`/devis/${quote.id}/modifier`)}
              >
                Modifier
              </Button>
              
              {quote.status === 'draft' && (
                <Button 
                  type="primary" 
                  icon={<CheckCircleOutlined />}
                  onClick={() => updateStatus('sent')}
                >
                  Envoyer
                </Button>
              )}
              
              {quote.status === 'sent' && (
                <>
                  <Button 
                    type="primary" 
                    icon={<CheckCircleOutlined />}
                    onClick={() => updateStatus('accepted')}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Accepter
                  </Button>
                  <Button 
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => updateStatus('rejected')}
                  >
                    Rejeter
                  </Button>
                </>
              )}
              
              {quote.status === 'accepted' && (
                <Button 
                  type="primary" 
                  icon={<ShoppingCartOutlined />}
                  onClick={handleConvertToOrder}
                >
                  Convertir en commande
                </Button>
              )}
              
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                onClick={handleDelete}
                loading={deleting}
              >
                Supprimer
              </Button>
            </>
          )}
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Détails" key="details">
          <Card style={{ marginBottom: 24 }}>
            <Descriptions 
              bordered 
              column={{ xs: 1, sm: 1, md: 2, lg: 3 }}
              size="small"
            >
              <Descriptions.Item label="Référence">
                <Text strong>{formatQuoteReference(quote.reference)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                <Text>{quote.customerName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Date d'émission">
                {dayjs(quote.issueDate).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Date d'expiration">
                {dayjs(quote.expiryDate).format('DD/MM/YYYY')}
                {dayjs(quote.expiryDate).isBefore(dayjs()) && (
                  <Tag color="red" style={{ marginLeft: 8 }}>Expiré</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <QuoteStatusTag status={quote.status} />
              </Descriptions.Item>
              {quote.status === 'accepted' && (
                <Descriptions.Item label="Paiement">
                  {quote.payment_status === 'paid' ? (
                    <Tag color="success">✓ Payé</Tag>
                  ) : quote.payment_status === 'deferred' ? (
                    <Tag color="blue" icon={<span>📅</span>}>Différé (le jour de l'intervention)</Tag>
                  ) : (
                    <Tag color="warning">⏳ En attente</Tag>
                  )}
                </Descriptions.Item>
              )}
              {quote.payment_type === 'split' && (
                <Descriptions.Item label="Paiement en 2 fois (50%)" span={2}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>1er paiement (acceptation) :</span>
                      {!isAgent && quote.first_payment_amount && (
                        <strong>{formatCurrency(quote.first_payment_amount)}</strong>
                      )}
                      {quote.first_payment_status === 'paid' ? (
                        <Tag color="success">✓ Payé</Tag>
                      ) : (
                        <Tag color="warning">En attente</Tag>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>2ème paiement (fin travaux) :</span>
                      {!isAgent && (quote.second_payment_amount || quote.first_payment_amount) && (
                        <strong>{formatCurrency(quote.second_payment_amount || quote.first_payment_amount)}</strong>
                      )}
                      {quote.second_payment_status === 'paid' ? (
                        <Tag color="success">✓ Payé</Tag>
                      ) : (
                        <Tag color="default">En attente</Tag>
                      )}
                    </div>
                  </div>
                </Descriptions.Item>
              )}
              {quote.scheduled_date && (
                <Descriptions.Item label="Date d'intervention">
                  <Text strong style={{ color: '#1890ff' }}>
                    {dayjs(quote.scheduled_date).format('DD/MM/YYYY à HH:mm')}
                  </Text>
                  {quote.execute_now ? (
                    <Tag color="green" style={{ marginLeft: 8 }}>Exécution immédiate</Tag>
                  ) : (
                    <Tag color="blue" style={{ marginLeft: 8 }}>Planifié par le client</Tag>
                  )}
                </Descriptions.Item>
              )}
              {quote.second_contact && (
                <Descriptions.Item label="Second contact">
                  <Text>{quote.second_contact}</Text>
                </Descriptions.Item>
              )}
              {quote.rejection_reason && (
                <Descriptions.Item label="Raison du refus" span={3}>
                  <Text type="danger">{quote.rejection_reason}</Text>
                </Descriptions.Item>
              )}
              {quote.notes && (
                <Descriptions.Item label="Notes" span={3}>
                  {quote.notes}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="Articles" style={{ marginBottom: 24 }}>
            <Table
              columns={columns}
              dataSource={quote.items || []}
              rowKey="id"
              pagination={false}
              size="middle"
              bordered
              summary={!isAgent ? ((data) => (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={4} index={0}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>{formatCurrency(quote.subtotal)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{formatCurrency(quote.discountAmount)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong>{formatCurrency(quote.taxAmount)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <Text strong>{formatCurrency(quote.total)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )) : undefined}
            />
            
            <Row gutter={24} style={{ marginTop: 24 }}>
              <Col xs={24} md={12}>
                {quote.termsAndConditions && (
                  <div style={{ marginTop: 24 }}>
                    <Text strong>Conditions générales :</Text>
                    <Paragraph style={{ whiteSpace: 'pre-line' }}>
                      {quote.termsAndConditions}
                    </Paragraph>
                  </div>
                )}
              </Col>
              <Col xs={24} md={12}>
                {!isAgent && (
                  <div style={{ textAlign: 'right' }}>
                    {stats.map((stat, index) => (
                      <div 
                        key={index} 
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 8,
                          maxWidth: 300,
                          marginLeft: 'auto',
                          ...(stat.isTotal ? { 
                            borderTop: '1px solid #f0f0f0', 
                            paddingTop: 12,
                            marginTop: 12,
                            fontSize: '1.1em',
                            fontWeight: 'bold' 
                          } : {})
                        }}
                      >
                        <span>{stat.title}:</span>
                        <span>{formatCurrency(stat.value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
          </Card>
        </TabPane>
        
        <TabPane tab="Historique" key="history">
          <Card>
            <p>Historique des modifications et des actions effectuées sur ce devis.</p>
            {/* À implémenter : afficher l'historique des modifications */}
          </Card>
        </TabPane>
        
        <TabPane tab="Documents liés" key="documents">
          <Card>
            <p>Documents associés à ce devis (bon de commande, facture, etc.).</p>
            {/* À implémenter : afficher les documents liés */}
          </Card>
        </TabPane>
      </Tabs>

      {/* Modal d'envoi d'email */}
      {!isAgent && (
        <Modal
          title={`Envoyer le devis ${quote.reference} par email`}
          visible={emailModalVisible}
          onOk={handleSendEmail}
          onCancel={() => setEmailModalVisible(false)}
          okText="Envoyer"
          cancelText="Annuler"
          width={600}
        >
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Destinataire :</label>
            <Input 
              value={emailData.to} 
              onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
              placeholder="email@client.com"
            />
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Objet :</label>
            <Input 
              value={emailData.subject} 
              onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
            />
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Message :</label>
            <Input.TextArea 
              rows={6}
              value={emailData.message} 
              onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
            />
          </div>
          
          <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f9f9f9', borderRadius: 4 }}>
            <p style={{ marginBottom: 8 }}><strong>Pièce jointe :</strong></p>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div>
                <div>devis-{quote.reference}.pdf</div>
                <Text type="secondary">PDF - {Math.round(quote.total * 100) / 100} KB</Text>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Version imprimable (cachée à l'écran) */}
      {!isAgent && (
        <div style={{ display: 'none' }}>
          <div ref={printRef} style={{ padding: '20mm', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ marginBottom: 20, textAlign: 'center' }}>
              <h1>Devis {quote.reference}</h1>
              <p>Date: {dayjs(quote.issueDate).format('DD/MM/YYYY')} - Validité jusqu'au: {dayjs(quote.expiryDate).format('DD/MM/YYYY')}</p>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 30 }}>
              <div>
                <h3>De la part de :</h3>
                <p><strong>MCT Maintenance</strong><br />
                Abidjan Marcory Zone 4 Rue du Canal<br />
                Tél: +225 0759505050<br />
                Email: smartmaintenance@mct.ci</p>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                <h3>À l'attention de :</h3>
                <p><strong>{quote.customerName}</strong><br />
                </p>
              </div>
            </div>
            
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 30 }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Produit</th>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Qté</th>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Prix unitaire</th>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Remise</th>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>TVA</th>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Total HT</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((item, index) => {
                  const total = item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100);
                  return (
                    <tr key={index}>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.productName}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>{item.quantity}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>{item.discount || 0}%</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>{item.taxRate || 0}%</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>{formatCurrency(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ width: '300px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Sous-total HT :</span>
                  <span>{formatCurrency(quote.subtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Remise :</span>
                  <span>-{formatCurrency(quote.discountAmount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>TVA :</span>
                  <span>{formatCurrency(quote.taxAmount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, paddingTop: 8, borderTop: '1px solid #000', fontWeight: 'bold', fontSize: '1.1em' }}>
                  <span>Total TTC :</span>
                  <span>{formatCurrency(quote.total)}</span>
                </div>
              </div>
            </div>
            
            {quote.notes && (
              <div style={{ marginTop: 30, padding: '15px', backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                <p><strong>Notes :</strong></p>
                <p>{quote.notes}</p>
              </div>
            )}
            
            {quote.termsAndConditions && (
              <div style={{ marginTop: 30 }}>
                <p><strong>Conditions générales :</strong></p>
                <p style={{ whiteSpace: 'pre-line', fontSize: '0.9em' }}>{quote.termsAndConditions}</p>
              </div>
            )}
            
            <div style={{ marginTop: 50, textAlign: 'center', fontSize: '0.9em', color: '#666' }}>
              <p>SMART MAINTENANCE - MCT - Abidjan Marcory Zone 4 Rue du Canal</p>
              <p>Tél: +225 0759505050 - Email: smartmaintenance@mct.ci</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetail;
